"use client";

import Image from "next/image";
import { TOURNAMENT_IDS_SEASON_1 } from "../../utils/constants";
import { greenStimpMap, Stimp } from "@/types/stimp";
import { useState } from "react";
import clsx from "clsx";

interface TeeProps {
  name: string;
  par: number;
  courseDistance: number;
}

export interface SettingsProps {
  femaleTee: TeeProps;
  maleTee: TeeProps;
  gameSettings: any;
}

export interface CourseProps {
  description: string;
  displayName: string;
  image: { url: string };
  numbersOfHoles: number;
  tees: Array<TeeProps>;
}
interface CourseAndSettingsProps {
  courseSettings: {
    round: { course: CourseProps; settings: SettingsProps };
    rounds: {
      course: CourseProps;
      settings: SettingsProps;
      embeddedGame: { longestDrive: { holes: any } };
    }[];
  };
  tournamentId: string;
  tournamentName: string;
  tournamentSeason: number;
}

const MAX_FIELD = 1000;
const MAX_FIELD_FINAL = 100;
const MAX_FIELD_FIRST_SEASON = {
  [TOURNAMENT_IDS_SEASON_1[0]]: 250,
  [TOURNAMENT_IDS_SEASON_1[1]]: 500,
  [TOURNAMENT_IDS_SEASON_1[2]]: 500,
  [TOURNAMENT_IDS_SEASON_1[3]]: 500,
  [TOURNAMENT_IDS_SEASON_1[4]]: 500,
  [TOURNAMENT_IDS_SEASON_1[5]]: 100,
};

const CourseAndSettings = ({
  courseSettings,
  tournamentId,
  tournamentName,
  tournamentSeason,
}: CourseAndSettingsProps) => {
  const hasMultipleRounds =
    courseSettings?.rounds && courseSettings?.rounds?.length > 1;
  const [activeRoundIdx, setActiveRoundIdx] = useState(0);

  const {
    greenFirmness,
    greenStimp,
    pinDifficulty,
    fairwayFirmness,
    windSpeed,
    puttingMode,
  } = courseSettings?.rounds?.[activeRoundIdx]?.settings?.gameSettings || {};

  const { description, displayName, image, numbersOfHoles } =
    courseSettings?.rounds?.[activeRoundIdx]?.course || {};
  const { maleTee, femaleTee } =
    courseSettings?.rounds?.[activeRoundIdx]?.settings;
  const selectedHoles =
    courseSettings?.rounds?.[activeRoundIdx]?.embeddedGame?.longestDrive?.holes;

  const convertMeterToYards = (meters: number) =>
    meters ? meters * 1.09361 : meters;

  const getTeeInfo = (name: string, courseDistance: number) => {
    return {
      name,
      yards: Math.round(convertMeterToYards(courseDistance)),
      meters: Math.round(courseDistance),
    };
  };

  const tees = courseSettings?.round?.course?.tees;
  const teesInfo = tees.map(({ name, courseDistance }: TeeProps) =>
    getTeeInfo(name, courseDistance)
  );

  const menTeeInfo = teesInfo.find((t: any) => t.name === maleTee?.name);
  const femaleTeeInfo = teesInfo.find((t: any) => t.name === femaleTee?.name);

  const transformCapitalized = (value: string) => {
    return `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`;
  };

  const mapFairwayFirmness =
    fairwayFirmness === "NORMAL" ? "MEDIUM" : fairwayFirmness;
  const mapGreenFirmness =
    greenFirmness === "NORMAL" ? "MEDIUM" : greenFirmness;

  const isFinalSeason2 =
    tournamentSeason === 2 && tournamentName?.toLowerCase()?.includes("final");

  return (
    <div>
      {/* Tabs for rounds */}
      {hasMultipleRounds && (
        <div className="mb-10 flex justify-center md:justify-start gap-2.5">
          {courseSettings?.rounds?.map((_: any, idx: number) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveRoundIdx(idx)}
              className={clsx(
                `py-2.5 px-5 text-[15px] uppercase tracking-[1.5px] rounded-md transition-colors`,
                {
                  "text-tm-c-brand bg-tm-c-gray-900": activeRoundIdx === idx,
                  "text-tm-c-gray-500 bg-tm-c-gray-950 hover:cursor-pointer hover:text-white hover:bg-tm-c-gray-900":
                    activeRoundIdx !== idx,
                }
              )}
            >
              {`Round ${idx + 1}`}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-7.5 mb-7.5 lg:grid-cols-2 lg:items-center">
        <div className="relative w-full h-[250px] min-[500px]:h-[350px] md:h-[400px] lg:h-[530px] rounded-[10px] overflow-hidden">
          {image?.url && (
            <Image
              src={image.url}
              fill
              sizes="100vw"
              className="object-cover"
              quality={100}
              alt={displayName || "course image"}
            />
          )}
        </div>
        <div className="grid gap-2.5 md:gap-7.5 text-center lg:text-left">
          <div className="text-2xl md:text-4xl">
            <span>{displayName}</span>
          </div>
          <div className="text-base 2xl:text-xl">
            <span>{description}</span>
          </div>
        </div>
      </div>
      <div className="grid gap-5 lg:gap-[30px] grid-cols-[100%] items-stretch lg:grid-cols-[calc(60%-5px)_calc(40%-5px)]">
        <div className="py-5 px-5 lg:py-[30px] lg:px-20 border border-tm-c-gray-700 rounded-[10px] bg-tm-c-gray-950 md:grid md:content-center md:justify-center h-full">
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-xs lg:text-base text-tm-c-gray-300 mb-[30px]">
            <div>Settings</div>
            <div className="text-right">Men / Women</div>
          </div>
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Tee</div>
            <div className="flex flex-col text-right">
              <span>
                {`${menTeeInfo?.name} ${menTeeInfo?.yards} yds / ${femaleTeeInfo?.name} ${femaleTeeInfo?.yards} yds`}
              </span>
              <span>
                {`${menTeeInfo?.name} ${menTeeInfo?.meters} m / ${femaleTeeInfo?.name} ${femaleTeeInfo?.meters} m`}
              </span>
            </div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Fairway Firmness</div>
            <div className="text-right">
              {transformCapitalized(mapFairwayFirmness)}
            </div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Green Firmness</div>
            <div className="text-right">
              {transformCapitalized(mapGreenFirmness)}
            </div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Pins</div>
            <div className="text-right">
              {transformCapitalized(pinDifficulty)}
            </div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Wind</div>
            <div className="text-right">{transformCapitalized(windSpeed)}</div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Putting</div>
            <div className="text-right">
              {transformCapitalized(puttingMode.split("_").join(" "))}
            </div>
          </div>
          <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
          <div className="grid grid-cols-[2fr_3fr] xl:grid-cols-[2fr_5fr] text-base">
            <div>Stimp</div>
            <div className="text-right">
              {greenStimpMap[greenStimp as Stimp]}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 grid-rows-2 auto-rows-fr gap-5 lg:gap-[30px] text-base">
          <div className="flex flex-col h-full justify-center items-center gap-1.5 py-[65px] px-2 border border-tm-c-gray-700 rounded-[10px] bg-tm-c-gray-950">
            <h2 className="m-0 text-2xl lg:text-4xl">{maleTee?.par}</h2>
            <span>Par</span>
          </div>
          <div className="flex flex-col h-full justify-center items-center gap-1.5 lg:gap-2.5 py-[65px] px-2 border border-tm-c-gray-700 rounded-[10px] bg-tm-c-gray-950">
            <h2 className="m-0 text-2xl lg:text-4xl">{numbersOfHoles}</h2>
            <span>Holes</span>
          </div>

          <div className="flex flex-col h-full justify-center items-center gap-1.5 py-[65px] px-2 border border-tm-c-gray-700 rounded-[10px] bg-tm-c-gray-950">
            <h2 className="m-0 text-2xl lg:text-4xl">
              {selectedHoles?.[0] ? `Hole ${selectedHoles?.[0]}` : "-"}
            </h2>
            <span>Longest Drive</span>
          </div>

          <div className="flex flex-col h-full justify-center items-center gap-1.5 py-[65px] px-2 border border-tm-c-gray-700 rounded-[10px] bg-tm-c-gray-950">
            <h2 className="m-0 text-2xl lg:text-4xl">
              {MAX_FIELD_FIRST_SEASON[tournamentId as any] || isFinalSeason2
                ? MAX_FIELD_FINAL
                : MAX_FIELD}
            </h2>
            <span>Max field</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseAndSettings;
